/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { deleteData, fetchData, postData, putData } from '@jda/lui-portal-utilities';
import { getAuthToken } from '../auth/authProvider';
import { convertObjectToQueryString, createRequestObject, getUserTokenRealmId, getAPIMUrl } from '../utility/commonUtility';
import { actionsPayload, IEntitiesForPolicyRule, IPolicy, IPolicyRequest, IPolicyRuleData, resourcePayload, resourceTypePayload } from '../models/Policies/PolicyContract';
import { IBuiltinPoliciesData, IBuiltinPoliciesSearch } from '../models/Policies/BuiltInPolicies';
import { DIMENSION_LEVEL_LIMIT } from '../shared/PolicyDialog/constants';

const { CLIENT_SERVICE_V1_URL, DATA_PLATFORM_SERVICE, CLIENT_SERVICE_V2_URL } = window['env'];

export const getAllPolicies = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/policies`;

  return fetchData<IPolicy[]>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const getPoliciesForRole = async (roleId: string | number, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/policies`;

  return fetchData<IPolicy[]>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  )
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const assignNewPolicyToRole = async (roleId: number, data: IPolicyRequest) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/policies`;

  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const createNewPolicy = async (roleId: number, data: IPolicyRuleData) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/policies`;

  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error.response.data.message;
    });
};

export const editPolicy = async (roleId: number, policy_id: number, data: IPolicyRuleData) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/policies/${policy_id}`;

  return putData<any>(createRequestObject(apiURL), data, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error.response.data.message;
    });
};


export const deletePolicyForRole = async (id: number | string, roleId: string | number) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/policies/${id}`;
  return deleteData(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getBuiltinPoliciesForGlobalRole = async (roleId: number, locationName?: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/builtinPolicies`;
  return fetchData<any[]>(
    createRequestObject(apiURL),
    locationName
      ? {
        headers: {
          'LOCATION-CONTEXT': locationName
        }
      }
      : getAuthToken
  );
};

export const disassociateBuiltinPolicy = async (roleId: number, policyId: any) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/builtinPolicies/${policyId}`;

  return deleteData(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch((error: any) => {
      throw error.response.data.message || error.message;
    });
};

export const associateBuiltinPoliciesToGlobalRole = async (roleId: number, builtinPolicyIds: number[]) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/roles/${roleId}/builtinPolicies`;
  return postData<any>(createRequestObject(apiURL), { builtinPolicyIds }, getAuthToken);
};

export const getEntitiesForPolicyRule = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/interactive/hierarchyDefinitions/v1`;
  return fetchData<IEntitiesForPolicyRule>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const getProductHierarchyForPolicyRule = async (requestPayload: any) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/interactive/itemHierarchyLevelMembers/v1/advancedQuery`;
  return postData<any>(createRequestObject(apiURL), requestPayload, getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const getEntitiesForPolicy = async (modelVersion: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/umm/v1/dp/models/BYDM/${modelVersion}/entities`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};
export const getDPModelApi = async () => {
  const realmId = await getUserTokenRealmId();
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/realm/v1/${realmId}/model`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};
export const getResourceNSApi = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/resourceNamespaces`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const getResourcesApi = async (payload: resourcePayload) => {
  const { namespaceId, pageNo, pageSize, searchString } = payload;
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V2_URL}/resourceNamespaces/${namespaceId}/resources?filter-has-no-resource-type-category=true`;

  apiURL = searchString ? apiURL + `&filter-name=${searchString}` : apiURL;

  if (pageNo && pageSize) {
    apiURL = apiURL + `&page-number=${pageNo}&page-size=${pageSize}`
  }

  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => appsResponse)
    .catch(error => {
      throw error;
    });
};

export const getEntitiesAttributesForPolicy = async (namespaceId: number, id: number, name: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/resourceNamespaces/${namespaceId}/resources/${id || name}/schemas`;

  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error.response.data.message;
    });
};

export const getActionsApi = async (payload: actionsPayload) => {
  const { namespaceId } = payload;
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/resourceNamespaces/${namespaceId}/actions`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken);
};

export const getResourceTypesApi = async (payload: resourceTypePayload) => {
//   return [
//     {
//         "id": 1002000140,
//         "name": "logicalmodel-dimension.dataAccess",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000223,
//             1002000224,
//             1002000364,
//             1002000365
//         ],
//         "category": "policy"
//     },
//     {
//         "id": 1002000141,
//         "name": "logicalmodel-measuregroup",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000225,
//             1002000226,
//             1002000227,
//             1002000228,
//             1002000363,
//             1002000419
//         ],
//         "category": "policy"
//     },
//     {
//         "id": 1002000142,
//         "name": "logicalmodel-measure",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000225,
//             1002000226,
//             1002000227,
//             1002000228,
//             1002000363,
//             1002000419
//         ],
//         "category": "policy"
//     },
//     {
//         "id": 1002000161,
//         "name": "logicalmodel-dimension",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000207,
//             1002000208,
//             1002000206,
//             1002000302
//         ],
//         "category": "policy"
//     },
//     {
//         "id": 1002000162,
//         "name": "logicalmodel-dimension.hierachyAccess",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000207,
//             1002000208,
//             1002000206,
//             1002000302
//         ],
//         "category": "policy"
//     },
//     {
//         "id": 1002000405,
//         "name": "logicalmodel-dimension.hierarchyAccess",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000207,
//             1002000208,
//             1002000206,
//             1002000302
//         ],
//         "category": "policy"
//     },
//     {
//         "id": 1002000423,
//         "name": "logicalentity",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000302,
//             1002000207,
//             1002000208,
//             1002000206
//         ],
//         "category": "policy"
//     },
//     {
//         "id": 1002000424,
//         "name": "logicalmodel-dimension.attributeAccess",
//         "namespaceId": 1002000267,
//         "actionIds": [
//             1002000207,
//             1002000419
//         ],
//         "category": "policy"
//     },
// ];
  const { namespaceId, category } = payload;
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/resourceNamespaces/${namespaceId}/resourceTypes?category=${category}`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken);
};

export const getDPAttributesForPolicy = async (entityName: string, modelVersion: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/umm/v1/dp/models/BYDM/${modelVersion}/${entityName}/jsonSchema?securable=true`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error.response.data.message;
    });
};

export const getDPSecurableArrtibutes = async (entityName: string, modelVersion: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/umm/v1/dp/models/BYDM/${modelVersion}/${entityName}/dataSecurable?securable=NONE`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error.response.data.message;
    });
};

export const getDimensionsForPolicy = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/logicalmodelservice/v1/dimensions`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};
export const getHierarchyForDimension = async (id: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/logicalmodelservice/v1/dimensions/${id}/hierarchies`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};
export const getDimensionMembersData = async (id: string, level: string, filter: string, limit: number = DIMENSION_LEVEL_LIMIT, offset?: number) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/logicalmodelservice/v1/data/dimension/${id}s/level?level=${level}&limit=${limit}&offset=${offset || 0}`;
  if (filter !== '') {
    apiURL += `&filter=${encodeURIComponent(filter)}`;
  }

  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};
export const getMeasureGroups = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/logicalmodelservice/v1/measure-groups`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse.entities;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getMeasures = async () => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/logicalmodelservice/v1/measures`;
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse.entities;
    })
    .catch((error: any) => {
      throw error;
    });
};

export const getBuiltinPolicy = async (queryParams?: IBuiltinPoliciesSearch) => {
  let queryData = '';

  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/builtinPolicies`;
  if (queryParams && typeof queryParams === 'object') {
    queryData = convertObjectToQueryString(queryParams);
    apiURL = `${apiURL}?${queryData}`;
  }

  return fetchData<IBuiltinPoliciesData>(createRequestObject(apiURL), getAuthToken)
    .then(response => {
      return response;
    })
    .catch((error: any) => {
      throw error;
    });
};
export const createBuiltinPolicy = async (data: IPolicyRuleData) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/builtinPolicies`;

  return postData<any>(createRequestObject(apiURL), data, getAuthToken)
    .catch((error: any) => {
      throw error.response.data.message;
    });
};

export const editBuiltinPolicy = async (policy_id: number, data: IPolicyRuleData) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/builtinPolicies/${policy_id}`;

  return putData<any>(createRequestObject(apiURL), data, getAuthToken)
    .catch((error: any) => {
      throw error.response.data.message;
    });
};

export const deleteBuiltinPolicy = async (policy_id: number) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/builtinPolicies/${policy_id}`;

  return deleteData(createRequestObject(apiURL), getAuthToken)
    .catch((error: any) => {
      throw error.response.data.message;
    });
};

export const getRemoteUrlDataResult = async (remoteUrl: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  let apiURL = `${BASE_APIM_URL}/${remoteUrl}`;

  return fetchData(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};
export const getResourceTypeById = async (namespaceId: string, typeId: string) => {
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${CLIENT_SERVICE_V1_URL}/resourceNamespaces/${namespaceId}/resourceTypes/${typeId}`;
  
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};

export const getAttributes = async (dimensionId: string, type: string) => {
//   if (_dimensionId === 'BDELOCATIONDEMO') {
//   return {
//       "dimensionId": "BDELOCATIONDEMO",
//       "attributes": [
//           {
//             "dimensionId": "LOCATION",
//             "id": "SampleAttributeId",
//             "label": "SampleAttributeLabel",
//             "isKey": true,
//             "isPk": false,
//             "isModifiableByUser": true,
//             "isRequired": false,
//             "defaultValue": "SampleAttributeDefaultValue"
//           },
//           {
//             "dimensionId": "LOCATION",
//             "id": "SampleAttributeId2",
//             "label": "SampleAttributeLabel2",
//             "isKey": false,
//             "isPk": false,
//             "isModifiableByUser": true,
//             "isRequired": false,
//             "defaultValue": "SampleAttributeDefaultValue2"
//           },
//           {
//             "dimensionId": "LOCATION",
//             "id": "SampleAttributeId3",
//             "label": "SampleAttributeLabel3",
//             "isKey": false,
//             "isPk": false,
//             "isModifiableByUser": true,
//             "isRequired": false,
//             "defaultValue": "SampleAttributeDefaultValue3"
//           },
//           {
//             "dimensionId": "LOCATION",
//             "id": "SampleAttributeId4",
//             "label": "SampleAttributeLabel4",
//             "isKey": false,
//             "isPk": false,
//             "isModifiableByUser": true,
//             "isRequired": false,
//             "defaultValue": "SampleAttributeDefaultValue4"
//           },
//           {
//             "dimensionId": "LOCATION",
//             "id": "SampleAttributeId5",
//             "label": "SampleAttributeLabel5",
//             "isKey": false,
//             "isPk": false,
//             "isModifiableByUser": true,
//             "isRequired": false,
//             "defaultValue": "SampleAttributeDefaultValue5"
//           }
//         ]
//   }; 
// } else {
//   return {
//       "dimensionId": "LOCATION",
//       "attributes": []
//   };
// } 
  const BASE_APIM_URL = await getAPIMUrl();
  const apiURL = `${BASE_APIM_URL}${DATA_PLATFORM_SERVICE}/logicalmodelservice/v1/logicalModel/${type}/${dimensionId}/attributes`;
  
  return fetchData<any>(createRequestObject(apiURL), getAuthToken)
    .then(appsResponse => {
      return appsResponse;
    })
    .catch(error => {
      throw error;
    });
};